/**
 * math-lesson.ts
 *
 * Single entry point for ALL math KPs. Dispatches on kpId:
 *   - generator-backed KPs → runGenericMathKpLesson (parametric, infinite Q)
 *   - bank-backed KPs      → runBankKpLesson (handwritten JSON banks)
 *
 * Wired into KP_ROUTE for the 35 math KPs that did NOT have a dedicated
 * wrapper file. The 7 math KPs that DO have wrappers (math.mul.2x1 etc.)
 * stay on their existing routes for backwards-compat with mistake-book +
 * remediation flows that import them directly.
 */
import { runGenericMathKpLesson, type MathGenFn } from './lessons/_runner';
import { runBankKpLesson } from './lessons/_generic-bank';
import type { LearnDoneCallback } from './app';

// ── 三下 generators (math/) ─────────────────────────────────────────
import { generateAddCarry } from './generators/math/add-carry';
import { generateSubBorrow } from './generators/math/sub-borrow';
import { generateNumberThousand } from './generators/math/number-thousand';
import { generateFracCompare } from './generators/math/frac-compare';
import { generateDecimalBasic } from './generators/math/decimal-basic';
import { generateDecAdd } from './generators/math/dec-add';
import { generateAreaRectangle } from './generators/math/area-rectangle';
import { generateTimeDuration } from './generators/math/time-duration';
import { generateTimeClockReading } from './generators/math/time-clock-3xia';

// ── 三上 generators (math-gen3-up/) ────────────────────────────────
import { generateCount10000 } from './generators/math-gen3-up/count-10000';
import { generatePlaceValue } from './generators/math-gen3-up/place-value';
import { generateAddSubVerticalUp } from './generators/math-gen3-up/add-sub-vertical-up';
import { generateMul2x1Up } from './generators/math-gen3-up/mul-2x1-up';
import { generateDivNoRem } from './generators/math-gen3-up/div-no-rem';
import { generateTimeClock } from './generators/math-gen3-up/time-clock';

// ── 四上 generators (math-gen4-up/) ────────────────────────────────
import { generateLargeNumber } from './generators/math-gen4-up/large-number';
import { generateAddSubMulti } from './generators/math-gen4-up/add-sub-multi';
import { generateMul2x2Up } from './generators/math-gen4-up/mul-2x2';
import { generateMul3x1Up } from './generators/math-gen4-up/mul-3x1-up';
import { generateTwoStepUp } from './generators/math-gen4-up/two-step';
import { generateFracAddSameDenom } from './generators/math-gen4-up/frac-add-same-denom';
import { generateDecimal2Place } from './generators/math-gen4-up/decimal-2-place';
import { generateTimeComplex } from './generators/math-gen4-up/time-complex';

/**
 * kpId → generator. KPs in this map use parametric infinite-question lessons.
 * `as MathGenFn` cast is safe because every generator listed accepts
 * (seed, difficulty) and returns a GeneratedQuestion (FillQuestion or MCQQuestion
 * — both extend GeneratedQuestion).
 */
const MATH_GENERATORS: Record<string, MathGenFn> = {
  // 三下 mid-curriculum (grade 3.2)
  'math.calc.addsub-vertical': generateAddCarry as MathGenFn,
  'math.number.thousand': generateNumberThousand as MathGenFn,
  'math.fraction.compare': generateFracCompare as MathGenFn,
  'math.decimal.basic': generateDecimalBasic as MathGenFn,
  'math.decimal.addsub': generateDecAdd as MathGenFn,
  'math.area.rectangle': generateAreaRectangle as MathGenFn,
  'math.time.duration': generateTimeDuration as MathGenFn,
  'math.time.clock': generateTimeClockReading as MathGenFn,

  // 三上 backport (grade 3.1)
  'math.number.10000': generateCount10000 as MathGenFn,
  'math.place.value.4digits': generatePlaceValue as MathGenFn,
  'math.add.carry.multi': generateAddSubVerticalUp as MathGenFn,
  'math.sub.borrow.multi': generateSubBorrow as MathGenFn,
  'math.mul.2x1.review': generateMul2x1Up as MathGenFn,
  'math.div.1digit.no-rem': generateDivNoRem as MathGenFn,
  'math.time.elapsed': generateTimeClock as MathGenFn,

  // 四上 backport (grade 4.1)
  'math.large.number.4up': generateLargeNumber as MathGenFn,
  'math.add.sub.multi.4up': generateAddSubMulti as MathGenFn,
  'math.mul.2x2.4up': generateMul2x2Up as MathGenFn,
  'math.mul.3x1.4up': generateMul3x1Up as MathGenFn,
  'math.word.two-step.4up': generateTwoStepUp as MathGenFn,
  'math.frac.add.same-denom.4up': generateFracAddSameDenom as MathGenFn,
  'math.decimal.2place.4up': generateDecimal2Place as MathGenFn,
  'math.time.complex.4up': generateTimeComplex as MathGenFn,
};

/**
 * kpId → bank file path. KPs in this map use handwritten 50Q banks
 * (geometry / measurement / stats where parametric is awkward).
 */
const MATH_BANKS: Record<string, string> = {
  'math.fraction.basic': '/data/math-fraction-basic-康軒.json',
  'math.length.cm': '/data/math-length-康軒.json',
  'math.weight.kg': '/data/math-weight-康軒.json',
  'math.angle.basic': '/data/math-angle-康軒.json',
  'math.shape.rectangle': '/data/math-shape-rectangle-康軒.json',
  'math.shape.circle': '/data/math-shape-circle-康軒.json',
  'math.stats.bar': '/data/math-stats-bar-康軒.json',
  'math.stats.table': '/data/math-table-康軒.json',
  // CURRICULUM 的 KP id 是 `math.angle.protractor.4up`(src/learn/curriculum/
  // index.ts:544)— dispatcher 之前誤拼 `math.frac.angle.protractor.4up`,
  // 導致 router 拿真 KP id 來查時 miss、玩家進不了量角器卷,並讓
  // tests/learn/math-lesson.test.ts 的「dispatcher KP ⊆ CURRICULUM」斷言失敗。
  // bank file path 本身就叫 math-angle-protractor → 對齊到正確 KP id。
  'math.angle.protractor.4up': '/data/math-angle-protractor-康軒-四上.json',
  'math.volume.basic.4up': '/data/math-volume-up-康軒-四上.json',
  // grade 3.1 frac.unit has a bank too; included here for consistency.
  'math.frac.unit': '/data/math-fraction-intro-康軒.json',
  // grade 4.1 stats-bar uses the bank rebuilt by math-statistics-up author.
  'math.stats.bar.4up': '/data/math-statistics-up-康軒-四上.json',
  // ── stage6 wire: meow911 段考真題池(對齊既有 chapter)─────────────
  // 注:ch1 addsub 分類後 0 題 → 無 bank file,該 KP 未 wire
  'math.mul.2x1.meow911': '/data/meow911-math-ch2-mul.json',
  'math.div.remainder.meow911': '/data/meow911-math-ch3-div.json',
  'math.fraction.basic.meow911': '/data/meow911-math-ch4-fraction.json',
  'math.decimal.basic.meow911': '/data/meow911-math-ch5-decimal.json',
  'math.area.rectangle.meow911': '/data/meow911-math-ch6-area.json',
  'math.time.clock.meow911': '/data/meow911-math-ch7-time.json',
  'math.meow911.misc': '/data/meow911-math-misc-pool.json',
};

/**
 * Public KP id lists — exported so the route table and tests can verify
 * coverage without re-declaring strings.
 */
export const MATH_LESSON_KP_IDS: readonly string[] = Object.freeze([
  ...Object.keys(MATH_GENERATORS),
  ...Object.keys(MATH_BANKS),
]);

/**
 * Single math lesson entry — looks up generator OR bank and runs it.
 * Returns void (matches the LessonRunner signature in lesson-route-table).
 */
export function runMathLesson(
  container: HTMLElement,
  kpId: string,
  onDone: LearnDoneCallback,
): void {
  if (Object.prototype.hasOwnProperty.call(MATH_GENERATORS, kpId)) {
    const generator = MATH_GENERATORS[kpId];
    runGenericMathKpLesson(container, { subject: 'math', kpId, generator }, onDone);
    return;
  }
  if (Object.prototype.hasOwnProperty.call(MATH_BANKS, kpId)) {
    const bankUrl = MATH_BANKS[kpId];
    void runBankKpLesson(container, { subject: 'math', kpId, bankUrl }, onDone);
    return;
  }
  // Unknown KP — caller (dispatchLesson) should never hit this since KP_ROUTE
  // wires only the supported ids. Treat as escape exit to keep UI sane.
  onDone({ newUnlocks: [], exited: true });
}
